import { config } from './config'
import { clearTokens, loadTokens, refreshTokens, type Tokens } from './auth'

/**
 * The API client.
 *
 * Two things here are load-bearing rather than plumbing:
 *
 *  - `ApiError` keeps the server's `code` and `detail`. The API reports a lost
 *    race as 409 with the current values attached (design SS5.6), and the UI can
 *    only offer "reload and retry" if the client did not flatten that into a
 *    generic failure message.
 *  - the access token is refreshed *before* the request when it is known to be
 *    stale, not only in response to a 401. Refreshing reactively means every
 *    mutation after the hour mark fires twice, and a retried mutation is a
 *    mutation applied twice unless the server happens to be idempotent.
 */

export interface ApiErrorBody {
  error: { code: string; message: string; detail?: unknown }
}

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly detail: unknown

  constructor(status: number, code: string, message: string, detail?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.detail = detail
  }

  /** A lost optimistic-concurrency race, as opposed to a rejected request. */
  get isConflict(): boolean {
    return this.status === 409
  }

  /** The edit was refused because the split is already settled (requirement 8). */
  get isSettledImmutable(): boolean {
    return this.code === 'SETTLED_IMMUTABLE'
  }

  /** The revision the server actually holds, when it told us. */
  get currentRevision(): number | undefined {
    const detail = this.detail as { currentRevision?: unknown } | undefined
    return typeof detail?.currentRevision === 'number' ? detail.currentRevision : undefined
  }
}

/** Raised when there is no usable session; the router redirects to /login. */
export class NotAuthenticated extends Error {
  constructor() {
    super('Please sign in again.')
    this.name = 'NotAuthenticated'
  }
}

let inFlightRefresh: Promise<Tokens> | undefined

/**
 * Serialise refreshes. The dashboard fires several queries at once, so without
 * this each of them would redeem the same rotating refresh token and all but one
 * would be rejected as replayed.
 */
async function currentTokens(): Promise<Tokens> {
  const tokens = loadTokens()
  if (!tokens) throw new NotAuthenticated()
  if (tokens.expiresAt > Date.now()) return tokens
  if (!tokens.refreshToken) {
    clearTokens()
    throw new NotAuthenticated()
  }
  if (!inFlightRefresh) {
    inFlightRefresh = refreshTokens(tokens.refreshToken).finally(() => {
      inFlightRefresh = undefined
    })
  }
  try {
    return await inFlightRefresh
  } catch {
    clearTokens()
    throw new NotAuthenticated()
  }
}

async function authHeaders(): Promise<Record<string, string>> {
  if (import.meta.env.DEV && config.devUser) {
    return {
      'x-test-user-id': config.devUser.userId,
      'x-test-user-email': config.devUser.email,
      'x-test-user-name': config.devUser.displayName,
    }
  }
  const tokens = await currentTokens()
  // The *id* token, not the access token: the API's JWT authorizer is configured
  // against the user pool's id-token audience, and `email` / `name` only appear
  // there.
  return { authorization: `Bearer ${tokens.idToken}` }
}

export interface RequestOptions {
  method?: string
  body?: unknown
  query?: Record<string, string | number | boolean | undefined>
  signal?: AbortSignal
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const url = new URL(`${config.apiBaseUrl}${path}`, window.location.origin)
  for (const [key, value] of Object.entries(opts.query ?? {})) {
    if (value !== undefined) url.searchParams.set(key, String(value))
  }

  const headers: Record<string, string> = { ...(await authHeaders()) }
  if (opts.body !== undefined) headers['content-type'] = 'application/json'

  const res = await fetch(url.toString(), {
    method: opts.method ?? 'GET',
    headers,
    ...(opts.body !== undefined ? { body: JSON.stringify(opts.body) } : {}),
    ...(opts.signal ? { signal: opts.signal } : {}),
  })

  if (res.status === 204) return undefined as T

  const text = await res.text()
  const json: unknown = text ? safeParse(text) : undefined

  if (!res.ok) {
    if (res.status === 401) {
      clearTokens()
      throw new NotAuthenticated()
    }
    const body = json as Partial<ApiErrorBody> | undefined
    throw new ApiError(
      res.status,
      body?.error?.code ?? 'UNKNOWN',
      body?.error?.message ?? `Request failed with ${res.status}`,
      body?.error?.detail,
    )
  }

  return json as T
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    // A non-JSON body from the API is a bug, but surfacing the raw text is more
    // useful to whoever has to debug it than a parse exception.
    return { error: { code: 'BAD_RESPONSE', message: text.slice(0, 200) } }
  }
}

/** Upload a statement straight to S3 with the presigned POST the API minted. */
export async function uploadStatement(
  upload: { url: string; fields: Record<string, string> },
  file: File,
): Promise<void> {
  const form = new FormData()
  for (const [key, value] of Object.entries(upload.fields)) form.append(key, value)
  // `file` must be appended last: S3 ignores any field that follows it.
  form.append('file', file)
  const res = await fetch(upload.url, { method: 'POST', body: form })
  if (!res.ok) {
    const detail = await res.text().catch(() => '')
    // The most likely cause by far is the 2 MB `content-length-range` in the
    // policy, which S3 reports as a 400 with an XML body nobody can read.
    throw new Error(
      res.status === 400
        ? 'The upload was rejected. Statements must be a CSV under 2 MB.'
        : `Upload failed (${res.status}) ${detail.slice(0, 200)}`,
    )
  }
}
